import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  InjectionToken,
  INJECTOR,
  input,
  linkedSignal,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Einheiteneingabe,
  EinheiteneingabeEinheitMenge,
} from '../types/einheiteneingabe';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { chooseFn, ResolvableModal, UiCoreModule } from '@softline/ui-core';
import {
  catchError,
  combineLatestWith,
  concatMap,
  filter,
  firstValueFrom,
  of,
  pairwise,
  startWith,
  switchMap,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { EinheiteneingabeService } from '../services/einheiteneingabe.service';
import { handleRequestError } from '@softline/application';
import { ConnectionHttpService, isDefinedNotEmpty } from '@softline/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Dimension } from '../types/dimension';
import { FieldOkService } from '@softline/dynamic';
import { EinheiteneingabeParams } from '../types/params';

const SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE = new InjectionToken<
  FieldOkService<any, any, any>
>('SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE');

@Component({
  selector: 'soft-einheiteneingabe-modal',
  imports: [CommonModule, ReactiveFormsModule, UiCoreModule],
  templateUrl: './einheiteneingabe-modal.component.html',
  styleUrls: ['./einheiteneingabe-modal.component.scss'],
  providers: [
    {
      provide: SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE,
      useFactory: () => new FieldOkService(inject(ConnectionHttpService), 'masseinheitd'),
    },
  ],
})
export class EinheiteneingabeModalComponent
  extends ResolvableModal<Einheiteneingabe | null>
  implements OnInit
{
  private injector = inject(INJECTOR);
  private choose = chooseFn();
  private fokService = inject(SOFTLINE_TOKEN_UNIT_ENTRY_SERVICE);

  readonly selectedMassUnit = signal<any | null>(null);
  readonly selectedMassUnit$ = toObservable(this.selectedMassUnit);

  form?: FormGroup;
  readonly loading = signal(false);

  readonly params = input<EinheiteneingabeParams | undefined>(undefined);

  readonly internalParams = linkedSignal(() => this.params());

  private readonly value$ = toObservable(this.internalParams).pipe(
    filter((o): o is EinheiteneingabeParams => !!o),
    switchMap((params) => {
      this.loading.set(true);

      return this.service.initialisieren(params).pipe(
        map((value) => this.createForm(value)),
        switchMap((form) => {
          this.loading.set(false);

          return form.valueChanges.pipe(
            startWith(form.value),
            pairwise(),
            map(([previous, current]) => this.computeFormState(previous, current)),
            combineLatestWith(this.selectedMassUnit$),
            concatMap(([formState, massUnit]) => {
              return this.service
                .umrechnen(
                  {
                    ...formState,
                    erfassungsEinheit: {
                      einheit: {
                        id: params.idbasisEinheit,
                      } as EinheiteneingabeEinheitMenge['einheit'],
                      menge: null,
                    } as EinheiteneingabeEinheitMenge,
                  },
                  massUnit?.id ?? undefined
                )
                .pipe(
                  catchError((e) => {
                    handleRequestError(e, this.injector, { show: true });
                    this.loading.set(false);
                    return of(null);
                  })
                );
            })
          );
        }),
        catchError((e) => {
          handleRequestError(e, this.injector, { show: true });
          this.loading.set(false);
          return of(null);
        })
      );
    })
  );

  constructor(
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly service: EinheiteneingabeService
  ) {
    super();
  }

  ngOnInit(): void {
    this.value$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newFormState) => {
      this.form?.patchValue(newFormState as never, { emitEvent: false });
      this.form?.updateValueAndValidity({ emitEvent: false });
      this.cdRef.detectChanges();
    });
  }

  retry(): void {
    const params = this.internalParams();
    if (!params) return;
    this.internalParams.set({ ...params });
  }

  private createForm(value: Einheiteneingabe): FormGroup {
    this.form = this.fb.nonNullable.group({
      artikel: [value.artikel],
      ideinheitEingabe: [value?.ideinheitEingabe ?? null],
      idarteheingabe: [value?.idarteheingabe ?? null],
      summeEinheit: this.createEinheitMengeFormGroup(value.summeEinheit),
      erfassungsEinheit: this.createEinheitMengeFormGroup({
        ...value.erfassungsEinheit,
        menge: value.erfassungsEinheit?.menge ?? this.params()?.menge ?? 1,
      }),
      einheiten: this.fb.array<FormGroup>(
        value.einheiten.map((mengeEinheit) =>
          this.createEinheitMengeFormGroup(mengeEinheit)
        )
      ),
      dimensionen: value?.dimensionen
        ? this.createDimensionFormGroup(value.dimensionen)
        : [null],
    });

    return this.form!;
  }

  private createEinheitMengeFormGroup(
    mengeEinheit: EinheiteneingabeEinheitMenge,
    editable = true
  ): FormGroup {
    return new FormGroup({
      einheit: new FormControl(mengeEinheit.einheit),
      menge: new FormControl(mengeEinheit?.menge as never),
      editable: new FormControl(mengeEinheit?.editable ?? editable, {
        nonNullable: true,
      }),
    });
  }

  private createDimensionFormGroup(dimensions: Dimension): FormGroup {
    return new FormGroup({
      tiefeBez: new FormControl<string | null>(dimensions?.tiefeBez ?? null),
      tiefe: new FormControl<number | null>(dimensions?.tiefe),
      hoeheBez: new FormControl<string | null>(dimensions?.hoeheBez ?? null),
      hoehe: new FormControl<number | null>(dimensions?.hoehe),
      breiteBez: new FormControl<string | null>(dimensions?.breiteBez ?? null),
      breite: new FormControl<number | null>(dimensions?.breite),
    });
  }

  private computeFormState(
    previousFormValue: Einheiteneingabe,
    currentFormValue: Einheiteneingabe
  ): Einheiteneingabe {
    let ideinheitEingabe: number | null = currentFormValue?.ideinheitEingabe ?? null;

    for (const [index, einheitMenge] of previousFormValue['einheiten'].entries()) {
      const currentEinheitMenge = currentFormValue['einheiten'][index];
      if (einheitMenge.menge !== currentEinheitMenge?.menge) {
        ideinheitEingabe = currentEinheitMenge.einheit.id;
        break;
      }
    }

    return {
      ...currentFormValue,
      ideinheitEingabe: ideinheitEingabe ?? undefined,
    };
  }

  async openFormularModal(): Promise<void> {
    const response = await firstValueFrom(
      this.fokService.query({
        filter: '',
        multiValued: false,
        parameters: {},
      })
    );

    const modalResult = await this.choose({
      options: response.map((res) => {
        return {
          value: res,
          label: res?.begriff ?? `${res.id}`,
        };
      }),
    });

    if (!modalResult || modalResult === 'DISMISSED') return;

    this.selectedMassUnit.set(modalResult);
  }

  submit(): void {
    console.log('submit: ', this.form?.value);
    this.resolve(this.form?.value);
  }

  protected readonly isDefinedNotEmpty = isDefinedNotEmpty;
}
